import React, {
  useEffect,
  useState,
  useMemo,
  useCallback,
  useRef,
} from "react";
import ReactApexChart from "react-apexcharts";
import ZoomModal from "./heatmap/zoomModal";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import { radialDimensionDetails } from "../services/api-services";
import { toastEmitter } from "../utills/utilities";
import { API_RESPONSE } from "../utills/app-constant";
import Modal from "../admin/components/common/Model";
import { CHAT_ICON, MENU_ICON, ZOOM_ICON } from "../utills/image-constant";
import Remark from "../admin/components/Charts/Remark";

const RadialDimensionHorizontal = ({
  selectedCompanyId,
  selectedCampaignId,
  type,
  graphName,
  title = "Radial-Dimenstion",
}) => {
  const [rowData, setRowData] = useState(null);
  const [remarkText, setRemarkText] = useState("");
  const [isAttempt, setIsAttempt] = useState("");
  const [remark, setRemark] = useState("");
  const [remarkId, setRemarkId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [series, setSeries] = useState([]);
  const [categories, setCategories] = useState([]);
  const [totalImportance, setTotalImportance] = useState(0);
  const [totalCurrent, setTotalCurrent] = useState(0);
  const [totalFuture, setTotalFuture] = useState(0);
  const [showTextBox, setShowTextBox] = useState(false);
  const [note, setNote] = useState("");
  const [showRemarkModal, setShowRemarkModal] = useState(false);
  const [activeDimension, setActiveDimension] = useState(null);
  const modalRef = useRef(null);

  // const modalId = "zoomModal-RadialDimensionHorizontal";
  const modalId = `zoomModal-${title?.replace(/\s+/g, "_")}`;

  const chartOptions = useMemo(
    () => ({
      chart: { id: "dimension_details", type: "bar", toolbar: { show: false } },
      plotOptions: {
        bar: {
          horizontal: true,
          barHeight: "70%",
          dataLabels: { position: "top" },
          borderRadius: 0,
        },
      },
      dataLabels: {
        enabled: true,
        formatter: (val) => val.toFixed(2),
        offsetX: 15, // small gap to move number outside
        style: { fontSize: "12px", colors: ["#000"] },
        textAnchor: "start", // align text to start (right side)
      },
      colors: ["#557D39", "#F7BB1C", "#D42327"],
      xaxis: {
        categories,
        labels: { formatter: (value) => value?.toFixed(0) },
        axisBorder: { show: false },
        axisTicks: { show: false },
        tickAmount: 5,
        min: 0,
        max: 10,
      },
      yaxis: {
        labels: { show: true },
        axisBorder: { show: false },
        axisTicks: { show: false },
      },
      grid: { show: false },
      legend: { position: "top", horizontalAlign: "center" },
      stroke: { show: false },
    }),
    [categories]
  );

  const getRadialDimensionDetailsData = useCallback(async () => {
    try {
      setLoading(true);
      const response = await radialDimensionDetails(
        selectedCompanyId,
        selectedCampaignId,
        type,
        graphName
      );
      setLoading(false);

      if (response?.data?.responseCode === 200) {
        const dimensions = response?.data?.responseBody || [];
        setCategories(dimensions.map((item) => item.dimensionName));
        setSeries([
          {
            name: "Current Score",
            data: dimensions.map((item) => item.currentAverageValue),
          },
          {
            name: "Future Score",
            data: dimensions.map((item) => item.futureAverageValue),
          },
          {
            name: "Importance Score",
            data: dimensions.map((item) => item.importantAverageValue),
          },
        ]);
        setRemark(response?.data?.remark || "");
        setRemarkId(response?.data?.remarkId || null);
        setIsAttempt(response?.data?.isAttempt ?? 0);

        setTotalImportance(response?.data?.totalImportanceAverage || 0);
        setTotalCurrent(response?.data?.totalCurrentAverage || 0);
        setTotalFuture(response?.data?.totalFutureAverage || 0);
      } else {
        toastEmitter(
          response?.data?.responseCode === 404 ? "info" : "error",
          response?.data?.message || "No data found."
        );
      }
    } catch (err) {
      setLoading(false);
      toastEmitter("error", API_RESPONSE.MESSAGE_503);
    }
  }, [selectedCompanyId, selectedCampaignId, type, graphName]);

  useEffect(() => {
    if (selectedCompanyId) getRadialDimensionDetailsData();
  }, [selectedCompanyId, getRadialDimensionDetailsData]);

  const exportAsExcel = () => {
    const headers = ["Dimension", "Current", "Future", "Importance"];
    const rows = rowData.map((d) => [
      d.dimensionName,
      d.currentAverageValue,
      d.futureAverageValue,
      d.importantAverageValue,
    ]);
    const worksheet = XLSX.utils.aoa_to_sheet([headers, ...rows]);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Report");
    const buffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
    saveAs(
      new Blob([buffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      }),
      `${title}.xlsx`
    );
  };

  // PDF Export
  const exportAsPDF = async () => {
    const chartBox = document.getElementById(`chart-box-${title}`);
    if (!chartBox) return;
    chartBox.scrollTo(0, 0);
    await new Promise((r) => setTimeout(r, 200));

    const chartElement = chartBox.querySelector(".apexcharts-canvas");
    if (!chartElement) return;

    const canvas = await html2canvas(chartElement, {
      scale: 2,
      backgroundColor: "#fff",
      useCORS: true,
    });

    const imgData = canvas.toDataURL("image/png");
    const pdf = new jsPDF("l", "mm", "a4");

    const pdfWidth = pdf.internal.pageSize.getWidth(); // Width of the PDF
    const pdfHeight = pdf.internal.pageSize.getHeight(); // Height of the PDF

    const canvasWidth = canvas.width; // Width of the canvas image
    const canvasHeight = canvas.height; // Height of the canvas image

    // Scaling factor to make the image a bit smaller (e.g., scale down by 80%)
    const scaleFactor =
      Math.min(pdfWidth / canvasWidth, pdfHeight / canvasHeight) * 0.8; // 0.8 reduces the size to 80%

    const imgWidth = canvasWidth * scaleFactor; // Scaled width
    const imgHeight = canvasHeight * scaleFactor; // Scaled height

    // Calculate the offset to center the image
    const xOffset = (pdfWidth - imgWidth) / 2;
    const yOffset = (pdfHeight - imgHeight) / 2;

    // Add the image to the PDF, centered and scaled down
    pdf.addImage(imgData, "PNG", xOffset, yOffset, imgWidth, imgHeight);

    // Save the PDF
    pdf.save(`${title || "chart"}.pdf`);
  };

  const hideDropdownBeforeExport = () => {
    const dropdown = document.querySelector(".dropdown-menu.show");
    if (dropdown) dropdown.classList.remove("show");
  };
  const exportAsImage = async () => {
    hideDropdownBeforeExport();
    const chartBox = document.getElementById(`chart-box-${title}`);
    if (!chartBox) return;
    chartBox.scrollTo(0, 0);
    await new Promise((r) => setTimeout(r, 200));

    const chartElement = chartBox.querySelector(".apexcharts-canvas");
    if (!chartElement) return;

    const canvas = await html2canvas(chartElement, {
      scale: 2,
      backgroundColor: "#fff",
      useCORS: true,
    });

    const link = document.createElement("a");
    link.download = `${title || "chart"}.png`;
    link.href = canvas.toDataURL();
    link.click();
  };

  return (
    <div id={`chart-box-${title}`} className="card h-100">
      {loading ? (
        <div className="loader-container">
          <div className="loader"></div>
        </div>
      ) : (
        <>
          {/* Header with Toolbar */}
          <div className="d-flex justify-content-between align-items-center overall-text mb-3 p-2">
            <h5>Radial Dimension</h5>
            <div className="d-flex align-items-center">
              {/* Zoom */}
              <button
                title="Zoom In"
                data-bs-toggle="modal"
                data-bs-target={`#${modalId}`}
                className="border-0 bg-transparent me-3"
              >
                {/* <i className="bi bi-zoom-in"></i> */}
                <img src={ZOOM_ICON} alt="zoom" />
              </button>

              {/* Dropdown */}
              <div className="dropdown">
                <button
                  className="icon-btn bg-transparent"
                  type="button"
                  id="dropdownMenuButton"
                  data-bs-toggle="dropdown"
                  aria-expanded="false"
                >
                  {/* <i className="bi bi-three-dots"></i> */}
                  <img src={MENU_ICON} alt="menu" />
                </button>
                <ul
                  className="dropdown-menu dropdown-menu-end"
                  aria-labelledby="dropdownMenuButton"
                >
                  <li>
                    <button
                      className="dropdown-item d-flex align-items-center gap-2"
                      onClick={exportAsExcel}
                    >
                      <i className="bi bi-file-earmark-excel"></i> Download
                      Excel
                    </button>
                  </li>
                  <li>
                    <button
                      className="dropdown-item d-flex align-items-center gap-2"
                      onClick={exportAsPDF}
                    >
                      <i className="bi bi-file-earmark-pdf"></i> Export PDF
                    </button>
                  </li>
                  <li>
                    <button
                      className="dropdown-item d-flex align-items-center gap-2"
                      onClick={exportAsImage}
                    >
                      <i className="bi bi-image"></i> Download Image
                    </button>
                  </li>
                </ul>
              </div>
            </div>
          </div>

          {/* Chart */}
          <ReactApexChart
            options={chartOptions}
            series={series}
            type="bar"
            height={450}
          />

          {/* Totals */}
          <div className="d-flex justify-content-around rounded my-3">
            <div className="text-center">
              <h6>Total Current</h6>
              <h5 className="text-success">{totalCurrent?.toFixed(2)}</h5>
            </div>
            <div className="text-center">
              <h6>Total Future</h6>
              <h5 className="text-warning">{totalFuture?.toFixed(2)}</h5>
            </div>
            <div className="text-center">
              <h6>Total Importance</h6>
              <h5 className="text-danger">{totalImportance?.toFixed(2)}</h5>
            </div>
          </div>
          <div className="d-flex justify-content-end mb-2">
            <button
              title="Add Comment"
              onClick={() => {
                setShowRemarkModal(true);
                setTimeout(() => {
                  if (modalRef.current) modalRef.current.click();
                }, 0);
              }}
              className="icon-btn position-absolute border-0 bg-transparent"
              style={{
                bottom: "10px",
                right: "10px",
              }}
            >
              <img src={CHAT_ICON} alt="chat" />

              {/* <i className="bi bi-chat-dots"></i> */}
            </button>
          </div>
          {/* Remark Modal */}
          {showRemarkModal && (
            <Modal
              modalClick={modalRef}
              title="Radial Dimenstion Remark"
              buttonLable=""
            >
              <Remark
                selectedCompanyId={selectedCompanyId}
                selectedCampaignId={selectedCampaignId}
                type={type}
                graphName={graphName}
                initialRemark={remark || ""}
                remarkId={remarkId || ""}
                isAttempt={isAttempt || 0}
                setRemarkprops={setRemark}
                setRemarkIdProps={setRemarkId}
                onCloseModal={() => setShowRemarkModal(false)}
              />
            </Modal>
          )}
          {/* Zoom Modal */}
          <ZoomModal
            id={modalId}
            title="Radial Dimension Horizontal"
            chartOptions={{ ...chartOptions, chart: { type: "bar" } }}
            seriesData={series}
            height={500}
            remarkText={remarkText}
            setRemarkText={setRemarkText}
            showRemarkModal={showRemarkModal}
            setShowRemarkModal={setShowRemarkModal}
            modalRef={modalRef}
            exportSummaryToExcel={exportAsExcel}
            exportAsPDF={exportAsPDF}
            exportAsImage={exportAsImage}
          />
        </>
      )}
    </div>
  );
};

export default RadialDimensionHorizontal;
